import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button, Input, Label, Icon } from '@repo/ui';
import { useVerifyEmailWithTracking, useCheckEmailVerificationStatus } from '@xtnd/web/features/settings/queries';
import { emailVerificationApi } from '@xtnd/web/features/settings/api/emailVerificationApi';
import type {
  ConfigModalProps,
  EmailFormData,
  EmailModuleConfig,
  EmailVerificationStatus,
} from '@xtnd/web/shared/types/tenantSettingTypes';

interface EmailConfigModalProps extends ConfigModalProps {
  initialData?: EmailModuleConfig;
  onSave: (data: EmailFormData) => Promise<void>;
}

export const EmailConfigModal: React.FC<EmailConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}) => {

  const [formData, setFormData] = useState<EmailFormData>({
    operationsSupportEmail: '',
    technicalSupportEmail: '',
    firmInvoiceFromEmail: '',
    firmInvoiceBccEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<EmailFormData>>({});
  const [verificationStatus, setVerificationStatus] = useState<{
    [key: string]: EmailVerificationStatus;
  }>({});

  // Initialize form when modal opens or data changes
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        operationsSupportEmail: initialData.operationsSupportEmail || '',
        technicalSupportEmail: initialData.technicalSupportEmail || '',
        firmInvoiceFromEmail: initialData.firmInvoiceFromEmail || '',
        firmInvoiceBccEmail: initialData.firmInvoiceBccEmail || '',
      });
    } else if (isOpen && !initialData) {
      setFormData({
        operationsSupportEmail: '',
        technicalSupportEmail: '',
        firmInvoiceFromEmail: '',
        firmInvoiceBccEmail: '',
      });
    }
    setErrors({});
  }, [isOpen, initialData]);

  // Verification mutations
  const verificationMutation = useVerifyEmailWithTracking();

  // Handle verification mutation with optimistic updates
  const handleVerifyEmail = (email: string, fieldName: string) => {
    // Optimistically update verification status
    setVerificationStatus(prev => ({
      ...prev,
      [fieldName]: {
        email,
        status: 'Pending',
        isVerifying: true,
      },
    }));

    verificationMutation.mutate(
      { email, fieldName },
      {
        onSuccess: (result) => {
          setVerificationStatus(prev => ({
            ...prev,
            [fieldName]: {
              ...result,
              isVerifying: false,
            },
          }));
        },
        onError: () => {
          setVerificationStatus(prev => ({
            ...prev,
            [fieldName]: {
              email,
              status: 'Failed',
              isVerifying: false,
            },
          }));
        },
      }
    );
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Get verification status for an email field
  const getFieldVerificationStatus = (fieldName: string): EmailVerificationStatus | undefined => {
    return verificationStatus[fieldName];
  };

  // Check verification status for FROM email fields only
  const checkAllVerificationStatuses = useCallback(async () => {
    const fromEmailFields = ['firmInvoiceFromEmail'] as const; // Only FROM emails need verification

    for (const fieldName of fromEmailFields) {
      const email = formData[fieldName];
      if (email && validateEmail(email)) {
        try {
          const status = await emailVerificationApi.getVerificationStatus(email);
          setVerificationStatus(prev => ({
            ...prev,
            [fieldName]: status,
          }));
        } catch (error) {
          console.warn(`Failed to check verification status for ${fieldName}:`, error);
        }
      }
    }
  }, [formData, setVerificationStatus]);

  // Effect to check verification statuses when FROM emails change
  useEffect(() => {
    if (isOpen) {
      checkAllVerificationStatuses();
    }
  }, [isOpen, formData.firmInvoiceFromEmail, checkAllVerificationStatuses]); // Only watch FROM email field

  const validateForm = (): boolean => {
    const newErrors: Partial<EmailFormData> = {};

    if (!formData.operationsSupportEmail) {
      newErrors.operationsSupportEmail = 'Operations support email is required';
    } else if (!validateEmail(formData.operationsSupportEmail)) {
      newErrors.operationsSupportEmail = 'Invalid email format';
    }

    if (!formData.technicalSupportEmail) {
      newErrors.technicalSupportEmail = 'Technical support email is required';
    } else if (!validateEmail(formData.technicalSupportEmail)) {
      newErrors.technicalSupportEmail = 'Invalid email format';
    }

    if (!formData.firmInvoiceFromEmail) {
      newErrors.firmInvoiceFromEmail = 'Firm invoice from email is required';
    } else if (!validateEmail(formData.firmInvoiceFromEmail)) {
      newErrors.firmInvoiceFromEmail = 'Invalid email format';
    }

    if (!formData.firmInvoiceBccEmail) {
      newErrors.firmInvoiceBccEmail = 'Firm invoice BCC email is required';
    } else if (!validateEmail(formData.firmInvoiceBccEmail)) {
      newErrors.firmInvoiceBccEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        operationsSupportEmail: '',
        technicalSupportEmail: '',
        firmInvoiceFromEmail: '',
        firmInvoiceBccEmail: '',
      });
      setErrors({});
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      handleClose();
    } catch (error) {
      console.error('Failed to save email configuration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof EmailFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Helper component for verification status indicator
  const VerificationStatusIcon: React.FC<{ status?: EmailVerificationStatus }> = ({ status }) => {
    if (!status) return null;

    const getIcon = () => {
      switch (status.status) {
        case 'Success':
          return (
            <Icon name="HiCheckCircle" className="h-5 w-5 text-green-500" title="Email verified" />
          );
        case 'Pending':
          return status.isVerifying ? (
            <Icon
              name="HiClock"
              className="h-5 w-5 text-yellow-500 animate-spin"
              title="Verifying..."
            />
          ) : (
            <Icon name="HiClock" className="h-5 w-5 text-yellow-500" title="Verification pending" />
          );
        case 'Failed':
        case 'TemporaryFailure':
          return (
            <Icon name="HiXCircle" className="h-5 w-5 text-red-500" title="Verification failed" />
          );
        case 'NotStarted':
          return (
            <Icon
              name="HiExclamationCircle"
              className="h-5 w-5 text-xtnd-dark-300"
              title="Not verified"
            />
          );
        default:
          return null;
      }
    };

    return <div className="flex items-center">{getIcon()}</div>;
  };

  // Helper component for verification button
  const VerificationButton: React.FC<{
    fieldName: string;
    email: string;
    status?: EmailVerificationStatus;
  }> = ({ fieldName, email, status }) => {
    const isValidEmail = validateEmail(email);
    const isDisabled = !isValidEmail || verificationMutation.isPending || !email;
    const isVerifying =
      status?.isVerifying ||
      (verificationMutation.isPending && verificationMutation.variables?.fieldName === fieldName);

    const handleVerify = () => {
      if (isValidEmail && email) {
        handleVerifyEmail(email, fieldName);
      }
    };

    const getButtonText = () => {
      if (isVerifying) return 'Verifying...';
      if (status?.status === 'Success') return 'Re-verify';
      return 'Verify';
    };

    const getButtonVariant = (): 'primary' | 'outline' | 'secondary' => {
      if (status?.status === 'Success') return 'outline';
      return 'primary';
    };

    return (
      <Button
        type="button"
        variant={getButtonVariant()}
        size="sm"
        onClick={handleVerify}
        disabled={isDisabled}
        loading={isVerifying}
        className="ml-2 px-3 py-1 text-xs"
      >
        {getButtonText()}
      </Button>
    );
  };

  // Helper component for email field with verification
  const EmailFieldWithVerification: React.FC<{
    fieldName: keyof EmailFormData;
    label: string;
    placeholder: string;
    value: string;
    error?: string;
    required?: boolean;
    requiresVerification?: boolean; // New prop to control verification
  }> = ({
    fieldName,
    label,
    placeholder,
    value,
    error,
    required = false,
    requiresVerification = false,
  }) => {
    const status = requiresVerification ? getFieldVerificationStatus(fieldName) : undefined;

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label
            htmlFor={fieldName}
            className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
          >
            {label}
          </Label>
          {requiresVerification && (
            <div className="flex items-center space-x-2">
              <VerificationStatusIcon status={status} />
              {value && <VerificationButton fieldName={fieldName} email={value} status={status} />}
            </div>
          )}
        </div>
        <Input
          id={fieldName}
          type="email"
          autoComplete="email"
          value={value}
          onChange={e => handleInputChange(fieldName, e.target.value)}
          placeholder={placeholder}
          error={error}
          disabled={isSubmitting}
          required={required}
        />
        {requiresVerification && status?.status === 'Success' && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            ✓ Email verified and ready to send
          </p>
        )}
        {requiresVerification && status?.status === 'Failed' && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            ✗ Verification failed. Please check the email address and try again.
          </p>
        )}
        {requiresVerification && status?.status === 'Pending' && !status?.isVerifying && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
            ⏳ Verification email sent. Please check your inbox and click the verification link.
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogHeader>
        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
          Email Configuration
        </h3>
        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-200 mt-1">
          Configure email addresses for notifications and invoice delivery.
        </p>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6">
          <div className="space-y-4">
            <EmailFieldWithVerification
              fieldName="operationsSupportEmail"
              label="Operations Support Email"
              placeholder="operations@firm.com"
              value={formData.operationsSupportEmail}
              error={errors.operationsSupportEmail}
              required={true}
              requiresVerification={false}
            />

            <EmailFieldWithVerification
              fieldName="technicalSupportEmail"
              label="Technical Support Email"
              placeholder="tech@firm.com"
              value={formData.technicalSupportEmail}
              error={errors.technicalSupportEmail}
              required={true}
              requiresVerification={false}
            />

            <EmailFieldWithVerification
              fieldName="firmInvoiceFromEmail"
              label="Firm Invoice 'From' Email"
              placeholder="invoices@firm.com"
              value={formData.firmInvoiceFromEmail}
              error={errors.firmInvoiceFromEmail}
              required={true}
              requiresVerification={true}
            />

            <EmailFieldWithVerification
              fieldName="firmInvoiceBccEmail"
              label="Firm Invoice 'BCC' Email"
              placeholder="billing@firm.com"
              value={formData.firmInvoiceBccEmail}
              error={errors.firmInvoiceBccEmail}
              required={true}
              requiresVerification={false}
            />
          </div>

          {/* Verification Notice */}
          <div className="bg-xtnd-light dark:bg-xtnd-dark-800 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon
                name="HiExclamationCircle"
                className="h-5 w-5 text-xtnd-blue mt-0.5 flex-shrink-0"
              />
              <div className="text-sm">
                <p className="font-medium text-xtnd-dark dark:text-xtnd-white mb-1">
                  FROM Email Verification Required
                </p>
                <p className="text-xtnd-dark-400 dark:text-xtnd-dark-200">
                  Only the "Firm Invoice 'From' Email" requires verification before it can be used
                  to send emails. Notification emails (Operations Support, Technical Support, BCC)
                  do not require verification. Click the "Verify" button next to the FROM email
                  address to send a verification email.
                </p>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
};
